import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageWrapper } from '../../components/layout';
import { ReportViews, ReportsTable } from '../../components/ai-reports-ledger';
import { ReportGeneratorDialog } from '../../components/ai-reports/ReportGeneratorDialog';
import { useToast } from '../../hooks/useToast';
import {
  REPORTS_PAGE_SIZE,
  ReportStatusFilter,
  useDeleteReport,
  useReportsPage,
} from '../../hooks/useReportQueries';
import aiReportsService from '../../services/ai-reports.service';
import ConfirmDialog from '../../components/common/ConfirmDialog';

const AIReportsPage: React.FC = () => {
  const navigate = useNavigate();

  const [status, setStatus] = useState<ReportStatusFilter>('ALL');
  const [page, setPage] = useState(1);
  const [generatorOpen, setGeneratorOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const toast = useToast();

  const { data, isPending, isError } = useReportsPage({ status, page });
  const rows = data?.results ?? [];
  const count = data?.count ?? 0;
  const deleteReport = useDeleteReport();

  useEffect(() => {
    if (isError) toast.error('Failed to load reports');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  // Deleting rows (or any refetch that shrinks the list) can leave the current
  // page past the end of the data — an empty slice with no pagination footer
  // and no way back. Clamp to the last populated page whenever that happens;
  // computing it from count lands directly on the right page even when several
  // pages vanish at once.
  useEffect(() => {
    if (!data) return;
    const lastPage = Math.max(1, Math.ceil(data.count / REPORTS_PAGE_SIZE));
    if (page > lastPage) setPage(lastPage);
  }, [data, page]);

  const selectStatus = (next: ReportStatusFilter) => {
    setStatus(next);
    setPage(1);
  };

  const openReport = (id: string) => navigate(`/ai-reports/${id}`);

  // The dialog's own mutation already invalidates the reports-page cache on
  // success, so a back-navigation won't show a stale pre-generation view —
  // this callback only needs to close the dialog and jump to the new report.
  const handleReportGenerated = (reportId: string) => {
    setGeneratorOpen(false);
    navigate(`/ai-reports/${reportId}`);
  };

  const handleDownload = async (id: string, downloadFormat: 'html' | 'pdf' | 'docx') => {
    try {
      await aiReportsService.downloadReport(id, downloadFormat);
    } catch {
      toast.error('Download failed');
    }
  };

  const closeDeleteDialog = () => setDeleteId(null);

  const confirmDelete = () => {
    if (!deleteId) return;
    const id = deleteId;
    closeDeleteDialog();
    deleteReport.mutate(id, {
      onSuccess: () => toast.success('Report deleted'),
      onError: () => toast.error('Delete failed'),
    });
  };

  return (
    <PageWrapper
      title="Reports"
      subtitle={isPending ? undefined : `${count} report${count === 1 ? '' : 's'}`}
      noPaper
      maxWidth="xl"
      actions={
        <button
          type="button"
          onClick={() => setGeneratorOpen(true)}
          className="cursor-pointer bg-accent px-3 py-1 text-scale-1 font-semibold text-paper hover:bg-accent-hover"
        >
          Generate report
        </button>
      }
    >
      <div className="pt-2">
        <ReportViews status={status} onStatusChange={selectStatus} />
      </div>

      <div className="mt-1">
        <ReportsTable
          rows={rows}
          count={count}
          page={page}
          isPending={isPending}
          hasNext={data?.next != null}
          hasPrevious={data?.previous != null}
          onOpen={openReport}
          onDelete={setDeleteId}
          onDownload={handleDownload}
          onPageChange={setPage}
        />
      </div>

      <ReportGeneratorDialog
        open={generatorOpen}
        onClose={() => setGeneratorOpen(false)}
        onGenerated={handleReportGenerated}
      />

      <ConfirmDialog
        open={deleteId != null}
        title="Delete report?"
        message="This action cannot be undone."
        confirmText="Delete"
        severity="error"
        onConfirm={confirmDelete}
        onCancel={closeDeleteDialog}
      />
    </PageWrapper>
  );
};

export default AIReportsPage;
